// Simple-mode cleaners (§11.19): one-tap fixes, each a toggle that shows a check mark when on; tapping
// again removes exactly what it changed (if the values are still the ones it set). Every tap is one undo
// step. All deterministic and non-AI, except "Remove background" (§11.12).
import { useEditor } from '../state/editor';
import { useLibrary, type LibraryPhoto } from '../state/library';
import { useSimple, type CleanerRecord } from '../state/simple';
import { defaultSettings } from '../model/defaults';
import { useUi } from '../state/ui';
import { autoFix, applyAutoFix, grayWorldWb } from './autofix';
import { renderForMetering } from './actions';
import { detectTilt } from './autoStraighten';
import { removeBackground } from './background/actions';
import { decodePool } from '../io/decode/pool';
import { straightenAngle, fitAspect, ASPECTS } from '../engine/geometry';
import { reframe } from '../ui/panels/GeometryPanel';
import { linearToSrgb } from '../color/transfer';
import type { MessageKey } from '../i18n';
import type { AspectId, Settings } from '../model/types';

export type CleanerId =
  | 'fixLighting'
  | 'fixColors'
  | 'brighten'
  | 'tameSky'
  | 'cleanPlate'
  | 'sharpen'
  | 'reduceGrain'
  | 'straighten'
  | 'removeSpots'
  | 'cropFor'
  | 'removeBackground';

type Ctx = { photo: LibraryPhoto; settings: Settings };
type Cleaner = {
  id: CleanerId;
  label: MessageKey;
  /** Settings paths this cleaner owns (for the toggle bookkeeping). */
  paths: string[];
  /** Applies the fix; returns a message key when nothing could be done. */
  run: (c: Ctx) => Promise<MessageKey | void> | MessageKey | void;
};

const get = (o: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((a, k) => (a as Record<string, unknown>)?.[k], o);
function put(o: unknown, path: string, v: unknown) {
  const keys = path.split('.');
  const last = keys.pop()!;
  (keys.reduce<unknown>((a, k) => (a as Record<string, unknown>)[k], o) as Record<string, unknown>)[last] =
    structuredClone(v);
}
const snapshot = (s: Settings, paths: string[]) =>
  Object.fromEntries(paths.map((p) => [p, structuredClone(get(s, p))]));
const same = (a: Record<string, unknown>, b: Record<string, unknown>) =>
  JSON.stringify(a) === JSON.stringify(b);
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const update = (id: string, fn: (s: Settings) => void) => useEditor.getState().update(id, fn);

/** Crop shapes offered by "Crop for…" [Recortar para…]. */
export const CROP_SHAPES: { aspect: AspectId; label: MessageKey }[] = [
  { aspect: '1:1', label: 'cropSquare' },
  { aspect: '4:5', label: 'cropPortrait' },
  { aspect: '9:16', label: 'cropStory' },
  { aspect: '3:2', label: 'cropMenu' },
];

export const CLEANERS: Cleaner[] = [
  {
    id: 'fixLighting',
    label: 'clFixLighting',
    paths: ['wb.temp', 'wb.tint', 'light.exposure', 'light.contrast', 'light.whites', 'light.blacks'],
    run: ({ photo }) => {
      const img = renderForMetering();
      if (!img) return 'clNothing';
      const r = autoFix(img);
      update(photo.id, (s) => Object.assign(s, applyAutoFix(s, r)));
    },
  },
  {
    id: 'fixColors',
    label: 'clFixColors',
    paths: ['wb.temp', 'wb.tint'],
    run: ({ photo }) => {
      const img = renderForMetering();
      if (!img) return 'clNothing';
      const wb = grayWorldWb(img, 1);
      update(photo.id, (s) => {
        s.wb.temp = wb.temp;
        s.wb.tint = wb.tint;
      });
    },
  },
  {
    id: 'brighten',
    label: 'clBrighten',
    paths: ['light.exposure', 'light.shadows', 'light.blacks'],
    run: ({ photo }) =>
      update(photo.id, (s) => {
        s.light.exposure = clamp(s.light.exposure + 0.6, -5, 5);
        s.light.shadows = clamp(s.light.shadows + 40, -100, 100);
        s.light.blacks = clamp(s.light.blacks + 5, -100, 100);
      }),
  },
  {
    id: 'tameSky',
    label: 'clTameSky',
    paths: ['light.highlights', 'light.whites'],
    run: ({ photo }) =>
      update(photo.id, (s) => {
        s.light.highlights = clamp(s.light.highlights - 60, -100, 100);
        s.light.whites = clamp(s.light.whites - 15, -100, 100);
      }),
  },
  {
    id: 'cleanPlate',
    label: 'clCleanPlate',
    paths: ['cleanWhites'],
    run: ({ photo }) => update(photo.id, (s) => void (s.cleanWhites = { amount: 70, strokes: null })),
  },
  {
    id: 'sharpen',
    label: 'clSharpen',
    paths: ['detail.sharpAmount', 'detail.sharpRadius', 'detail.sharpMasking'],
    run: ({ photo }) =>
      update(photo.id, (s) => Object.assign(s.detail, { sharpAmount: 60, sharpRadius: 1, sharpMasking: 40 })),
  },
  {
    id: 'reduceGrain',
    label: 'clReduceGrain',
    paths: ['detail.nrLuma', 'detail.nrColor'],
    run: ({ photo }) => update(photo.id, (s) => Object.assign(s.detail, { nrLuma: 40, nrColor: 50 })),
  },
  {
    id: 'straighten',
    label: 'clStraighten',
    paths: ['geometry'],
    run: async ({ photo }) => {
      const small = await decodePool().small(photo.file, 1024);
      const n = small.width * small.height;
      const gray = new Float32Array(n);
      for (let p = 0; p < n; p++) {
        const d = small.data;
        gray[p] = linearToSrgb(
          Math.min(
            1,
            Math.max(0, 0.2289746 * d[p * 4]! + 0.6917385 * d[p * 4 + 1]! + 0.0792869 * d[p * 4 + 2]!),
          ),
        );
      }
      const l = detectTilt(gray, small.width, small.height);
      if (!l) return 'clNoLine';
      const W = photo.width || small.width,
        H = photo.height || small.height;
      update(photo.id, (s) => {
        s.geometry.angle = straightenAngle(0, l.x0, l.y0, l.x1, l.y1);
        s.geometry.constrain = true;
        reframe(s, W, H, { refit: true });
      });
    },
  },
  {
    id: 'removeSpots',
    label: 'clRemoveSpots',
    paths: [],
    run: () => {
      // A tool, not a value: tap on spots; each tap is one heal spot (one undo step).
      const ui = useUi.getState();
      ui.set({ tool: ui.tool === 'spot' ? 'none' : 'spot' });
    },
  },
  {
    id: 'cropFor',
    label: 'clCropFor',
    paths: ['geometry'],
    run: () => undefined, // handled by cropFor(aspect)
  },
  {
    id: 'removeBackground',
    label: 'clRemoveBackground',
    paths: ['background.mode'],
    run: async ({ photo }) => {
      await removeBackground([photo.id]);
    },
  },
];

function active(): Ctx | null {
  const lib = useLibrary.getState();
  const photo = lib.photos.find((p) => p.id === lib.activeId);
  if (!photo) return null;
  return { photo, settings: useEditor.getState().get(photo.id) };
}

/** True when the cleaner's change is still in place (the check mark). */
export function cleanerOn(photoId: string, id: CleanerId, s: Settings): boolean {
  if (id === 'removeSpots') return useUi.getState().tool === 'spot';
  const rec = useSimple.getState().cleaners[photoId]?.[id];
  const c = CLEANERS.find((x) => x.id === id)!;
  return !!rec && same(rec.after, snapshot(s, c.paths));
}

function record(photoId: string, id: CleanerId, rec: CleanerRecord | null) {
  useSimple.setState((st) => {
    const mine = { ...(st.cleaners[photoId] ?? {}) };
    if (rec) mine[id] = rec;
    else delete mine[id];
    return { cleaners: { ...st.cleaners, [photoId]: mine } };
  });
}

/** Taps a cleaner: on → apply (one undo step); on again → restore what it changed (one undo step). */
export async function toggleCleaner(id: CleanerId): Promise<void> {
  const ctx = active();
  if (!ctx) return;
  const c = CLEANERS.find((x) => x.id === id)!;
  if (id === 'removeSpots') {
    await c.run(ctx);
    return;
  }
  if (cleanerOn(ctx.photo.id, id, ctx.settings)) {
    const rec = useSimple.getState().cleaners[ctx.photo.id]![id]!;
    update(ctx.photo.id, (s) => {
      for (const p of c.paths) put(s, p, rec.before[p]);
    });
    record(ctx.photo.id, id, null);
    return;
  }
  const before = snapshot(ctx.settings, c.paths);
  const msg = await c.run(ctx);
  const after = snapshot(useEditor.getState().get(ctx.photo.id), c.paths);
  if (msg) useSimple.getState().set({ message: msg });
  if (!same(before, after)) record(ctx.photo.id, id, { before, after });
}

/** "Crop for…": a centred crop of the chosen shape (constrain on); the photo can then be dragged inside. */
export function cropFor(aspect: AspectId) {
  const ctx = active();
  if (!ctx) return;
  const c = CLEANERS.find((x) => x.id === 'cropFor')!;
  const rec = useSimple.getState().cleaners[ctx.photo.id]?.cropFor;
  const before =
    rec && cleanerOn(ctx.photo.id, 'cropFor', ctx.settings) ? rec.before : snapshot(ctx.settings, c.paths);
  const W = ctx.photo.width || 1,
    H = ctx.photo.height || 1;
  update(ctx.photo.id, (s) => {
    s.geometry.aspect = aspect;
    s.geometry.constrain = true;
    s.geometry.crop = fitAspect(ASPECTS[aspect] ?? 1, W, H);
    reframe(s, W, H, { refit: false });
  });
  record(ctx.photo.id, 'cropFor', {
    before,
    after: snapshot(useEditor.getState().get(ctx.photo.id), c.paths),
  });
  useUi.getState().set({ tool: 'cropPan' });
}

/** Start over [Empezar de nuevo]: back to defaults (geometry too), one undo step. */
export function startOver() {
  const ctx = active();
  if (!ctx) return;
  useEditor.getState().update(ctx.photo.id, (s) => void Object.assign(s, defaultSettings()));
  useSimple.setState((st) => ({
    cleaners: { ...st.cleaners, [ctx.photo.id]: {} },
    filter: { ...st.filter, [ctx.photo.id]: undefined },
  }));
}
